/**
 * 📹 CCTV(감시) 사이징 — 엑셀 「Surveillance」 시트 + 「Surveillance database」 (v2.233.0)
 *   카메라 종류 3가지(형식·해상도·화질·fps·대수·하루 녹화 시간·보관 일수) → 대역폭(MB/s)·보관 용량(TB)
 *   → 컨트롤러(단일/이중) 판정 → 디스크 크기별 DS 1012/1016 + JBOD 추천 표
 * 엑셀은 4·6·8TB 만 보여 줬는데 숨은 표에는 1·2·3·5TB 도 있어 고를 수 있게 했다. 호스트 보드는 엑셀에서 숨은 시트에
 * 있던 선택(가격에만 영향)을 꺼내 놓은 것이다.
 *
 * 배치(v2.233.3): 폰 = 세로 한 줄 · PC(lg) = 카메라 표 한 줄 + [구성 | 결과] · 넓은 PC(1700px 이상) = [카메라 표 | 구성] + 결과 한 줄.
 *   카메라 표는 폭을 다 쓰면 드롭다운이 너무 길어져 칸마다 20rem 까지만.
 */
import { Fragment, useState } from 'react';
import { calcSurveillance } from '../../lib/sizing/calc';
import type { SvCamera, SvInput } from '../../lib/sizing/calc';
import {
  DRIVE_SIZES_TB, SV_CONTROLLER_LIMIT, SV_FPS, SV_HOST_BOARDS, SV_HOURS, SV_MAX_FPS, SV_QUALITIES, SV_QUALITY_LABEL,
  SV_RESOLUTIONS, SV_RESOLUTION_LABEL, SV_STREAMS, SV_STREAM_LABEL,
} from '../../lib/sizing/data';
import { Dash, Field, Formula, NumInput, Panel, SelectInput, SimpleTable, Stat, StatGrid, fmt } from './SizingUi';

const CAM1: SvCamera = { stream: 'H.264', resolution: '1.3 Megapixel', quality: 'Normal', fps: 15, count: 200, hoursPerDay: 24, days: 90 };
const CAM_NONE: SvCamera = { stream: 'N/A', resolution: 'D1 (704x480)', quality: 'Normal', fps: 1, count: 0, hoursPerDay: 1, days: 0 };

export function CctvCalc() {
  const [i, setI] = useState<SvInput>({ cameras: [CAM1, CAM_NONE, CAM_NONE], spare: 1, hostBoard: 'none', drivePrices: {}, driveSizes: [4, 6, 8] });
  const r = calcSurveillance(i);
  const setCam = (idx: number, next: SvCamera) => setI({ ...i, cameras: i.cameras.map((c, k) => (k === idx ? next : c)) });
  const toggleSize = (tb: number) => {
    const has = i.driveSizes.includes(tb);
    const sizes = has ? i.driveSizes.filter((x) => x !== tb) : [...i.driveSizes, tb].sort((a, b) => a - b);
    setI({ ...i, driveSizes: sizes });
  };
  const controllerText = r.controller === 'single' ? '단일 컨트롤러 (G 모델)' : r.controller === 'dual' ? '이중 컨트롤러 (R 모델)' : 'DS 1000 제품군으로는 안 됨 (처리량 초과)';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] min-[1700px]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <Panel title="카메라 정보 — 종류별로 넣는다 (쓰지 않는 종류는 형식을 「없음」으로)" tone="input" className="lg:col-span-2 min-[1700px]:col-span-1"
          note={`최대 FPS 기준(엑셀 각주): 10MP 6 · 5MP 10 · 3MP 12 · 1080P 25 · 1.3MP 이하 30. 넘으면 경고만 하고 계산은 그대로 한다.`}>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-slate-600">
                  <th className="text-left px-2 py-1.5 w-48"></th>
                  {[0, 1, 2].map((k) => <th key={k} className="text-left px-2 py-1.5 font-semibold text-base text-slate-700">카메라 종류 {k + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                <CamRow label="1. 스트림 형식">{(k) => <SelectInput value={i.cameras[k]!.stream} options={SV_STREAMS.map((s) => ({ value: s, label: SV_STREAM_LABEL[s] }))} onChange={(stream) => setCam(k, { ...i.cameras[k]!, stream })} width="w-full" />}</CamRow>
                <CamRow label="2. 해상도">{(k) => <SelectInput value={i.cameras[k]!.resolution} options={SV_RESOLUTIONS.map((s) => ({ value: s, label: SV_RESOLUTION_LABEL[s] }))} onChange={(resolution) => setCam(k, { ...i.cameras[k]!, resolution })} width="w-full" />}</CamRow>
                <CamRow label="3. 화질 (기본 보통)">{(k) => <SelectInput value={i.cameras[k]!.quality} options={SV_QUALITIES.map((s) => ({ value: s, label: SV_QUALITY_LABEL[s] }))} onChange={(quality) => setCam(k, { ...i.cameras[k]!, quality })} width="w-full" />}</CamRow>
                <CamRow label="4. 카메라당 FPS">{(k) => (
                  <span className="flex items-center gap-1.5">
                    <SelectInput value={i.cameras[k]!.fps} options={SV_FPS.map((f) => ({ value: f, label: `${f} fps` }))} onChange={(fps) => setCam(k, { ...i.cameras[k]!, fps })} width="w-full" />
                    {r.perCamera[k]!.overMaxFps && <span className="text-red-600 text-base" title={`${SV_RESOLUTION_LABEL[i.cameras[k]!.resolution]} 최대 ${SV_MAX_FPS[i.cameras[k]!.resolution]}fps`}>⚠</span>}
                  </span>
                )}</CamRow>
                <CamRow label="5. 카메라 수">{(k) => <NumInput value={i.cameras[k]!.count} min={0} step={1} onChange={(count) => setCam(k, { ...i.cameras[k]!, count })} suffix="대" width="w-28" />}</CamRow>
                <CamRow label="6. 하루 녹화 시간">{(k) => <SelectInput value={i.cameras[k]!.hoursPerDay} options={SV_HOURS.map((h) => ({ value: h, label: `${h}시간` }))} onChange={(hoursPerDay) => setCam(k, { ...i.cameras[k]!, hoursPerDay })} width="w-full" />}</CamRow>
                <CamRow label="7. 보관 일수">{(k) => <NumInput value={i.cameras[k]!.days} min={0} step={1} onChange={(days) => setCam(k, { ...i.cameras[k]!, days })} suffix="일" width="w-28" />}</CamRow>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="구성 · 가격 (선택)" tone="input" note="디스크 가격은 정가 합계에 「디스크 수 × 가격」으로 더해진다. 모르면 0 으로 두면 장비 정가만 나온다.">
          <Field label="인클로저(서랍)당 스페어"><SelectInput value={i.spare} options={[{ value: 0, label: '0개' }, { value: 1, label: '1개' }]} onChange={(spare) => setI({ ...i, spare })} /></Field>
          <Field label="호스트 보드" hint="가격에만 영향 · 계산은 온보드 1GbE 기준"><SelectInput value={i.hostBoard} options={SV_HOST_BOARDS.map((h) => ({ value: h.key, label: h.label }))} onChange={(hostBoard) => setI({ ...i, hostBoard })} /></Field>
          <div className="py-1.5">
            <div className="text-sm text-slate-700 mb-1.5">표에 볼 디스크 크기</div>
            <div className="flex flex-wrap gap-2">
              {DRIVE_SIZES_TB.map((tb) => (
                <button key={tb} type="button" onClick={() => toggleSize(tb)}
                  className={`btn btn-sm ${i.driveSizes.includes(tb) ? 'btn-success-solid' : 'btn-secondary'}`}>
                  {tb}TB
                </button>
              ))}
            </div>
          </div>
          {i.driveSizes.map((tb) => (
            <Field key={tb} label={`${tb}TB 디스크 1개 가격`}><NumInput value={i.drivePrices[tb] ?? 0} min={0} step={1} onChange={(v) => setI({ ...i, drivePrices: { ...i.drivePrices, [tb]: v } })} suffix="USD" /></Field>
          ))}
        </Panel>

        <Panel title="결과 — 대역폭 · 보관 용량 · 컨트롤러" tone="result" className="min-[1700px]:col-span-2">
          <StatGrid>
            {r.perCamera.map((c, k) => (
              i.cameras[k]!.stream === 'N/A' ? null : (
                <Fragment key={k}>
                  <Stat label={`종류 ${k + 1} 대역폭`} value={fmt(c.mbPerSec, 1)} unit="MB/s" sub={`1fps 당 ${fmt(c.perCameraPerFps, 3)} Mbps × ${i.cameras[k]!.fps}fps × ${fmt(i.cameras[k]!.count)}대`} />
                  <Stat label={`종류 ${k + 1} 보관 용량`} value={fmt(c.storageTb, 1)} unit="TB" sub={`${i.cameras[k]!.hoursPerDay}시간 × ${fmt(i.cameras[k]!.days)}일 · 여유 10%`} />
                </Fragment>
              )
            ))}
            <Stat label="총 대역폭" value={fmt(r.totalMbPerSec, 1)} unit="MB/s" big />
            <Stat label="총 보관 용량" value={fmt(r.totalStorageTb, 1)} unit="TB" big />
            <Stat label="컨트롤러" value={controllerText} sub={`${SV_CONTROLLER_LIMIT.single} MB/s 까지 단일 · ${SV_CONTROLLER_LIMIT.dual} 미만 이중`} big tone={r.controller === null ? 'warn' : 'ok'} />
          </StatGrid>
          <Formula>
            <p>대역폭(Mbps) = 1fps 당 Mbps(형식·해상도·화질 표) × fps × 카메라 수 · MB/s = Mbps ÷ 8</p>
            <p>보관 용량(TB) = Mbps × 3,600 × 하루 시간 ÷ 8 ÷ 1,024 × 일수 ÷ 1,024 × 1.1 (10% 여유)</p>
            <p>컨트롤러 = 총 MB/s 가 352 까지면 단일(G) · 668 미만이면 이중(RC) · 그 위는 DS 1000 으로는 안 됨 (랜덤 1M · 온보드 1GbE 기준)</p>
          </Formula>
        </Panel>
      </div>

      <Panel title="추천 구성 — 디스크 크기별 · RAID 6 (엑셀 표 그대로)" tone="result" note="모든 결과는 온보드 1GbE iSCSI 호스트 인터페이스 기준. 이중화 모델은 캐시 백업 모듈(슈퍼캡 + 플래시)이 기본이며 BBU + 플래시도 가능. 정가(USD)는 2015~2017년 당시 값. 「—」는 JBOD 연결 한도(12·16베이 14대 · 48베이 서랍 18 · 60베이 서랍 15)를 넘어 그 구성으로는 안 되는 경우.">
        <SimpleTable
          head={['디스크', 'RAID 모델', 'JBOD', '구성', '총 디스크', '사용 가능(TB)', '스페어', '높이(U)', '정가 합계($)', '$ / TB']}
          align={['c', 'l', 'l', 'c', 'r', 'r', 'r', 'r', 'r', 'r']}
          rows={r.rows.map((x) => [
            `${x.driveTb}TB`, x.system, x.jbod,
            x.config ?? <Dash />, x.totalDrives === null ? <Dash /> : fmt(x.totalDrives), x.usableTb === null ? <Dash /> : fmt(x.usableTb, 1),
            x.spares === null ? <Dash /> : fmt(x.spares), x.heightU === null ? <Dash /> : fmt(x.heightU),
            x.msrp === null ? <Dash /> : fmt(x.msrp), x.msrpPerTb === null ? <Dash /> : fmt(x.msrpPerTb, 1),
          ])}
        />
        <Formula>
          <p>필요 디스크 = 올림(총 보관 TB × 1,048,576 ÷ (디스크 실제 MB − 256) ÷ (1 − 0.2%))</p>
          <p>데이터 디스크 = 인클로저(서랍)마다 베이 − 2(RAID 6 패리티) − 스페어 · JBOD 수 = 올림(모자라는 디스크 ÷ JBOD 1대(서랍 1개)의 데이터 디스크)</p>
          <p>사용 가능(TB) = 데이터 디스크 × 디스크 1개 실제 TB · 정가 = RAID 모델 + JBOD × 대수 + 호스트 보드 × 컨트롤러 수 + 디스크 가격 × 총 디스크</p>
        </Formula>
      </Panel>
    </div>
  );
}

function CamRow({ label, children }: { label: string; children: (k: number) => React.ReactNode }) {
  return (
    <tr className="border-t border-emerald-200/60">
      {/* 폰에서 표를 옆으로 밀어도 줄 이름은 남아 있게 — 안 그러면 「무슨 칸인지」를 잃는다 (10-08 검토) */}
      <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap sticky left-0 bg-emerald-50/95 md:bg-transparent md:static">{label}</td>
      {[0, 1, 2].map((k) => <td key={k} className="px-2 py-1.5 min-w-[11rem]"><div className="max-w-[20rem]">{children(k)}</div></td>)}
    </tr>
  );
}
